import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * useAimlapiTranscription — voice-activity-detected (VAD) speech-to-text
 * via aimlapi's `/v1/stt/create` endpoint. Implements pseudo-realtime
 * streaming on top of a batch API:
 *
 *   1. Capture mic via getUserMedia + MediaRecorder (webm/opus)
 *   2. Continuously monitor audio level through an AnalyserNode
 *   3. When the user starts speaking, begin recording a "segment"
 *   4. When the user stays silent for > silenceMs, stop the current
 *      segment, upload it to aimlapi, poll for the transcript, and
 *      append the result to the running transcript
 *   5. The user can keep speaking — each natural sentence becomes
 *      its own segment, so the user sees subtitles arriving every
 *      time they take a breath instead of waiting until the end
 *
 * The key trade-off vs. true streaming WebSocket APIs (Deepgram /
 * AssemblyAI realtime / 阿里云实时 ASR): every segment incurs an
 * upload + queue + poll round-trip, so subtitle latency is 1-3
 * seconds *per natural pause*, not a few hundred milliseconds. The
 * benefit is that we can use the same key/infrastructure as the
 * rest of the app and don't need a streaming proxy.
 */

const AIMLAPI_KEY  = '83a2f266f9832f26013791f8958570dc'
const AIMLAPI_HOST = 'https://api.aimlapi.com'
const STT_MODEL    = '#g1_whisper-large'

// VAD tuning. These were picked empirically against the macOS `say`
// test clips and Chrome on macOS. If your mic gain is unusually
// high/low you may want to adjust SILENCE_RMS.
/** RMS amplitude (0..1) below which we consider the mic "silent". */
const SILENCE_RMS  = 0.012
/** Milliseconds of continuous silence required before we close
 *  the current speech segment and ship it off for transcription. */
const SILENCE_MS   = 800
/** Minimum segment duration before VAD will close it — keeps very
 *  short coughs / clicks from being uploaded as their own segments. */
const MIN_SPEECH_MS = 350

interface SttCreateResponse {
  generation_id: string
  status: string
}

interface SttPollResponse {
  generation_id: string
  status: 'queued' | 'processing' | 'completed' | 'failed' | string
  result?: {
    results?: {
      channels?: {
        alternatives?: { transcript?: string }[]
      }[]
    }
  }
}

async function transcribeBlob(blob: Blob, signal: AbortSignal): Promise<string> {
  // eslint-disable-next-line no-console
  console.log('[stt] uploading blob', { size: blob.size, type: blob.type })
  const form = new FormData()
  form.append('model', STT_MODEL)
  form.append('audio', blob, 'segment.webm')
  const createRes = await fetch(`${AIMLAPI_HOST}/v1/stt/create`, {
    method:  'POST',
    headers: { Authorization: `Bearer ${AIMLAPI_KEY}` },
    body:    form,
    signal,
  })
  if (!createRes.ok) {
    const body = await createRes.text().catch(() => '')
    // eslint-disable-next-line no-console
    console.warn('[stt] create failed', createRes.status, body)
    throw new Error(`stt/create failed: ${createRes.status}`)
  }
  const created: SttCreateResponse = await createRes.json()
  // eslint-disable-next-line no-console
  console.log('[stt] queued', created.generation_id)
  if (!created.generation_id) throw new Error('stt/create: no generation_id')

  const start = Date.now()
  while (Date.now() - start < 12_000) {
    if (signal.aborted) throw new DOMException('aborted', 'AbortError')
    await new Promise((r) => setTimeout(r, 600))
    const pollRes = await fetch(`${AIMLAPI_HOST}/v1/stt/${created.generation_id}`, {
      headers: { Authorization: `Bearer ${AIMLAPI_KEY}` },
      signal,
    })
    if (!pollRes.ok) continue
    const polled: SttPollResponse = await pollRes.json()
    if (polled.status === 'completed') {
      const text = polled.result?.results?.channels?.[0]?.alternatives?.[0]?.transcript ?? ''
      // eslint-disable-next-line no-console
      console.log('[stt] completed', { generation_id: created.generation_id, text })
      return text.trim()
    }
    if (polled.status === 'failed') {
      throw new Error('stt: transcription failed')
    }
  }
  throw new Error('stt: poll timed out')
}

export interface UseAimlapiTranscriptionResult {
  /** Whether the browser exposes the APIs we need. */
  supported: boolean
  /** Whether we're currently capturing the microphone. */
  listening: boolean
  /** All finalized transcript segments concatenated. */
  transcript: string
  /** Live indicator: how many in-flight segments are still being
   * transcribed. > 0 means a sentence is on the wire. */
  pendingSegments: number
  /** Most recent error message (network failure, mic denied, etc). */
  error: string | null
  /** Begin a recording session. Resolves once the mic is open. */
  start: () => Promise<void>
  /** End the recording session gracefully — closes the current
   * segment and uploads it. */
  stop: () => void
  /** End the session and discard all in-flight + pending segments. */
  abort: () => void
  /** Wipe transcript / pending counter / error so the next session
   * starts fresh. */
  reset: () => void
  /** Imperatively (re-)request mic permission via getUserMedia so
   * the browser surfaces its native permission prompt. Returns true
   * on grant. */
  requestPermission: () => Promise<boolean>
}

export function useAimlapiTranscription(): UseAimlapiTranscriptionResult {
  const supported = typeof window !== 'undefined'
    && typeof MediaRecorder !== 'undefined'
    && typeof navigator !== 'undefined'
    && !!navigator.mediaDevices?.getUserMedia

  const [listening,       setListening]       = useState(false)
  const [transcript,      setTranscript]      = useState('')
  const [pendingSegments, setPendingSegments] = useState(0)
  const [error,           setError]           = useState<string | null>(null)

  // Refs hold all the imperative audio plumbing — none of these need
  // to drive React renders.
  const streamRef       = useRef<MediaStream | null>(null)
  const audioCtxRef     = useRef<AudioContext | null>(null)
  const analyserRef     = useRef<AnalyserNode | null>(null)
  const recorderRef     = useRef<MediaRecorder | null>(null)
  const rafRef          = useRef<number | null>(null)
  const speakingRef     = useRef(false)
  const speechStartedAt = useRef(0)
  const lastVoiceAt     = useRef(0)
  const abortCtrlRef    = useRef<AbortController | null>(null)
  const stoppingRef     = useRef(false)
  /** Pending-segment counter mirror — refs let us decrement
   *  synchronously inside async callbacks without React re-renders
   *  driving stale closures. */
  const pendingCountRef = useRef(0)

  const setPending = (delta: number) => {
    pendingCountRef.current = Math.max(0, pendingCountRef.current + delta)
    setPendingSegments(pendingCountRef.current)
  }

  /** Take the current MediaRecorder, stop it, and ship the resulting
   *  blob off to aimlapi. Each segment runs concurrently so a slow
   *  poll doesn't block the user from speaking the next sentence. */
  const finalizeSegment = useCallback(() => {
    const recorder = recorderRef.current
    if (!recorder || recorder.state === 'inactive') return

    // eslint-disable-next-line no-console
    console.log('[stt] finalizeSegment: stopping recorder, state=', recorder.state)
    setPending(+1)
    const ctrl = abortCtrlRef.current!
    recorder.ondataavailable = async (ev) => {
      // eslint-disable-next-line no-console
      console.log('[stt] segment ondataavailable', { size: ev.data?.size })
      if (!ev.data || ev.data.size === 0) {
        setPending(-1)
        return
      }
      try {
        const text = await transcribeBlob(ev.data, ctrl.signal)
        // eslint-disable-next-line no-console
        console.log('[stt] segment text:', text)
        if (text) {
          setTranscript((prev) => {
            const next = prev ? `${prev} ${text}` : text
            // eslint-disable-next-line no-console
            console.log('[stt] setTranscript', { prev, next })
            return next
          })
        }
      } catch (e) {
        if ((e as Error).name !== 'AbortError') {
          // eslint-disable-next-line no-console
          console.warn('[stt] segment failed:', e)
          setError((e as Error).message)
        }
      } finally {
        setPending(-1)
      }
    }
    try {
      recorder.stop()
    } catch { /* already stopped */ }
  }, [])

  /** Start a fresh MediaRecorder for the next speech segment. */
  const startNewRecorder = useCallback(() => {
    const stream = streamRef.current
    if (!stream) return
    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : ''
    const recorder = mimeType
      ? new MediaRecorder(stream, { mimeType })
      : new MediaRecorder(stream)
    recorderRef.current = recorder
    recorder.start()
    speakingRef.current     = false
    speechStartedAt.current = 0
    lastVoiceAt.current     = 0
  }, [])

  /** RAF loop that watches the analyser's RMS amplitude and toggles
   *  the speaking/silent state machine. When silence persists past
   *  SILENCE_MS we close the current segment and immediately spin up
   *  a new recorder for the next sentence. */
  const tick = useCallback(() => {
    const analyser = analyserRef.current
    if (!analyser) return
    const buf = new Float32Array(analyser.fftSize)
    analyser.getFloatTimeDomainData(buf)
    let sum = 0
    for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i]
    const rms = Math.sqrt(sum / buf.length)

    const now = performance.now()
    if (rms > SILENCE_RMS) {
      if (!speakingRef.current) {
        speakingRef.current     = true
        speechStartedAt.current = now
      }
      lastVoiceAt.current = now
    } else if (speakingRef.current) {
      const sinceVoice = now - lastVoiceAt.current
      const sinceStart = now - speechStartedAt.current
      if (sinceVoice >= SILENCE_MS && sinceStart >= MIN_SPEECH_MS) {
        // Close out the current segment, ship it, start a new one
        // so the user can keep talking without missing a beat.
        finalizeSegment()
        startNewRecorder()
      }
    }

    if (!stoppingRef.current) {
      rafRef.current = requestAnimationFrame(tick)
    }
  }, [finalizeSegment, startNewRecorder])

  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!supported) {
      setError('not-supported')
      return false
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      stream.getTracks().forEach((t) => t.stop())
      setError(null)
      return true
    } catch (e) {
      const name = e instanceof Error ? e.name : ''
      setError(name === 'NotAllowedError' || name === 'SecurityError'
        ? 'not-allowed'
        : name === 'NotFoundError'
          ? 'audio-capture'
          : 'permission-failed')
      // eslint-disable-next-line no-console
      console.warn('[stt] permission request failed:', e)
      return false
    }
  }, [supported])

  const start = useCallback(async () => {
    if (!supported) {
      setError('not-supported')
      return
    }
    if (streamRef.current) return // already running
    setError(null)
    setTranscript('')
    pendingCountRef.current = 0
    setPendingSegments(0)
    stoppingRef.current = false
    abortCtrlRef.current = new AbortController()

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream

      const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
      const ctx = new AC()
      audioCtxRef.current = ctx
      const source   = ctx.createMediaStreamSource(stream)
      const analyser = ctx.createAnalyser()
      analyser.fftSize = 1024
      analyser.smoothingTimeConstant = 0.4
      source.connect(analyser)
      analyserRef.current = analyser

      startNewRecorder()
      setListening(true)
      rafRef.current = requestAnimationFrame(tick)
    } catch (e) {
      const name = e instanceof Error ? e.name : ''
      setError(name === 'NotAllowedError' || name === 'SecurityError'
        ? 'not-allowed'
        : name === 'NotFoundError'
          ? 'audio-capture'
          : 'start-failed')
      // eslint-disable-next-line no-console
      console.warn('[stt] start failed:', e)
    }
  }, [supported, startNewRecorder, tick])

  const teardown = useCallback((discard: boolean) => {
    stoppingRef.current = true
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }

    if (discard) {
      // ─── Cancel path ──────────────────────────────────────────
      // Throw away anything in flight and immediately tear down the
      // mic + audio graph. Caller asked to discard.
      abortCtrlRef.current?.abort()
      const r = recorderRef.current
      if (r && r.state !== 'inactive') {
        r.ondataavailable = null
        try { r.stop() } catch { /* noop */ }
      }
      recorderRef.current = null
      streamRef.current?.getTracks().forEach((t) => t.stop())
      streamRef.current = null
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => {})
        audioCtxRef.current = null
      }
      analyserRef.current = null
      setListening(false)
      return
    }

    // ─── Graceful flush path ──────────────────────────────────────
    // eslint-disable-next-line no-console
    console.log('[stt] teardown(graceful): flushing final segment, recorder.state=', recorderRef.current?.state)
    const r = recorderRef.current
    if (r && r.state !== 'inactive') {
      // Replace ondataavailable with one that both ships the blob
      // for transcription AND tears down the audio plumbing on its
      // way out. Wraps the existing finalizeSegment logic inline
      // so we can sequence the teardown after it.
      setPending(+1)
      const ctrl = abortCtrlRef.current!
      r.ondataavailable = async (ev) => {
        try {
          if (ev.data && ev.data.size > 0) {
            const text = await transcribeBlob(ev.data, ctrl.signal)
            if (text) {
              setTranscript((prev) => (prev ? `${prev} ${text}` : text))
            }
          }
        } catch (e) {
          if ((e as Error).name !== 'AbortError') {
            // eslint-disable-next-line no-console
            console.warn('[stt] final segment failed:', e)
            setError((e as Error).message)
          }
        } finally {
          setPending(-1)
        }
      }
      try { r.stop() } catch { /* noop */ }
    }
    recorderRef.current = null

    // It's safe to tear down the stream / context synchronously
    // here: MediaRecorder.stop() has already been called above,
    // which causes the browser to flush its internal buffer and
    // fire `dataavailable` *before* the underlying tracks are
    // released. The async callback above runs after that, and
    // by then the data is already in `ev.data` (a Blob — fully
    // copied, no longer dependent on the live stream).
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {})
      audioCtxRef.current = null
    }
    analyserRef.current = null
    setListening(false)
  }, [])

  const stop = useCallback(() => {
    teardown(false)
  }, [teardown])

  const abort = useCallback(() => {
    teardown(true)
    setTranscript('')
    pendingCountRef.current = 0
    setPendingSegments(0)
  }, [teardown])

  const reset = useCallback(() => {
    setTranscript('')
    setError(null)
    pendingCountRef.current = 0
    setPendingSegments(0)
  }, [])

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      teardown(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    supported,
    listening,
    transcript,
    pendingSegments,
    error,
    start,
    stop,
    abort,
    reset,
    requestPermission,
  }
}
